import cx from 'clsx';
import React, { useRef } from 'react';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as TableSort } from '@assets/svg/table-sort.svg';

import ButtonGoUp from '@components/common/ButtonGoUp/ButtonGoUp';
import TableEmptyRow from '@components/common/TableEmptyRow/TableEmptyRow';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';

import { initialSorting } from '@constants/tableSorting';

import useTranslation from '@hooks/useTranslation';

import {
  selectAssetHealthEquipmentSorting,
  selectAssetHealthUrgency,
  useAssetHealthOverviewStoreActions,
} from '@pages/ExecutiveSummary/store/useAssetHealthOverviewFilterStorePersist';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { useAssetHealthEquipmentListQuery } from '../../services/useAssetHealthEquipmentListQuery';
import ExpandedRow from '../ExpandedRow/ExpandedRow';
import EquipmentTableSkeleton from './EquipmentTableSkeleton';
import style from './style.module.scss';

const EquipmentTable = () => {
  const { t } = useTranslation();
  const equipmentTableContainerRef = useRef(null);

  const { setAssetHealthEquipmentSorting } = useAssetHealthOverviewStoreActions();
  const locationSelectValue = selectAppLocationSelectValue();
  const sorting = selectAssetHealthEquipmentSorting();
  const urgency = selectAssetHealthUrgency();

  const { data, isLoading } = useAssetHealthEquipmentListQuery({
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    sorting,
    urgency: urgency.value,
  });

  const onChangeSorting = (key: string) => {
    if (!sorting.id || sorting.id !== key) {
      setAssetHealthEquipmentSorting({ id: key, desc: false });
      return;
    }
    const { id, desc } = sorting;
    if (desc) {
      setAssetHealthEquipmentSorting(initialSorting);
    } else {
      setAssetHealthEquipmentSorting({ id, desc: true });
    }
  };

  return (
    <>
      <div className={style.equipmentTableWrapper} ref={equipmentTableContainerRef}>
        <table className={style.equipmentTable}>
          <thead>
            <tr>
              <th>
                {t('Location')}
                <TableSort
                  className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'location_name' })}
                  onClick={() => onChangeSorting('location_name')}
                  data-tooltip-content="location_name"
                  data-tooltip-id="equipment-sort-tooltip"
                />
              </th>
              <th>
                {t('Equipment')}
                <TableSort
                  className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'equipment_name' })}
                  onClick={() => onChangeSorting('equipment_name')}
                  data-tooltip-content="equipment_name"
                  data-tooltip-id="equipment-sort-tooltip"
                />
              </th>
              <th>
                {t('Critical')}
                <TableSort
                  className={cx(style.sortIcon, {
                    [style.activeSort]: sorting.id === 'critical_regular_ais_count',
                  })}
                  onClick={() => onChangeSorting('critical_regular_ais_count')}
                  data-tooltip-content="critical_regular_ais_count"
                  data-tooltip-id="equipment-sort-tooltip"
                />
              </th>
              <th>
                {t('High')}
                <TableSort
                  className={cx(style.sortIcon, {
                    [style.activeSort]: sorting.id === 'high_regular_ais_count',
                  })}
                  onClick={() => onChangeSorting('high_regular_ais_count')}
                  data-tooltip-content="high_regular_ais_count"
                  data-tooltip-id="equipment-sort-tooltip"
                />
              </th>
              <th>
                {t('Standard')}
                <TableSort
                  className={cx(style.sortIcon, {
                    [style.activeSort]: sorting.id === 'standard_regular_ais_count',
                  })}
                  onClick={() => onChangeSorting('standard_regular_ais_count')}
                  data-tooltip-content="standard_regular_ais_count"
                  data-tooltip-id="equipment-sort-tooltip"
                />
              </th>
              <th>
                {t('Total')}
                <TableSort
                  className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'total_ais_count' })}
                  onClick={() => onChangeSorting('total_ais_count')}
                  data-tooltip-content="total_ais_count"
                  data-tooltip-id="equipment-sort-tooltip"
                />
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <EquipmentTableSkeleton />
            ) : data?.length ? (
              data?.map((row) => <ExpandedRow row={row} key={row.equipment_id} />)
            ) : (
              <TableEmptyRow text={t('No equipment were found.')} />
            )}
          </tbody>
        </table>
        <TooltipSort id="equipment-sort-tooltip" sorting={sorting} />
        <Tooltip id="equipment-cell-tooltip" place="top-start" />
      </div>
      <div className={style.tableFooter}>
        <ButtonGoUp ref={equipmentTableContainerRef} />
      </div>
    </>
  );
};

export default EquipmentTable;
